import { ExceptionUtils } from "#src/utils/exception";
import { usePrevious } from "@validereinc/common-components";
import {
  KeyValueStorageError,
  serializeWithMapSupport,
  deserializeWithMapSupport,
  containsMaps,
} from "@validereinc/utilities";
import { nanoid } from "nanoid";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SetStateAction,
} from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

/**
 * Parse a value from storage.
 * @param val the value to parse.
 * @param defaultValue the default value to return if the value is null.
 * @returns the parsed value.
 */
function parseStorageValue<T>(
  val: string | null,
  defaultValue: T,
  {
    key,
    parser = (value) => deserializeWithMapSupport(value),
  }: {
    /** the key of the value in storage. */
    key: string;
    /** an override to the parsing that needs to happen when the value is ready from storage. default is deserializeWithMapSupport(). */
    parser?: (valueFromStorageSystem: string) => T;
  }
): T {
  try {
    return val !== null ? parser(val) : defaultValue;
  } catch (err: unknown) {
    console.error(err);

    if (
      (err as DOMException)?.name === "InvalidCharacterError" ||
      (err as SyntaxError)?.name === "SyntaxError"
    ) {
      const normalizedErr = new KeyValueStorageError(
        "Sticky state was not JSON parse-able, falling back to default value",
        {
          props: {
            value: val,
            defaultValue,
            key,
          },
          cause: err,
          getExplanation: () =>
            "Cache could not be understood. Reverting to default configuration.",
        }
      );
      console.warn(normalizedErr);
      ExceptionUtils.reportException(normalizedErr, "warning", {
        sourceComponent: "parseStorageValue() in useStickyState()",
      });
    }

    // return the default value, even if JSON parsing fails
    return defaultValue;
  }
}

/**
 * Get a value from local or session storage
 * @param key the key to store the value under in storage
 * @param defaultValue the first value to store. similar concept to useState.
 * @returns the value at the key in the storage location, run through the parser
 */
export const getFromStorage = <T>(
  key: string,
  defaultValue: T,
  {
    storageSystem = window.localStorage,
    parser = (value) => JSON.parse(value) as T,
  }: {
    /** the browser storage system to use e.g. window.localStorage or window.sessionStorage. default is local storage. */
    storageSystem?: Storage;
    /** an override to the parsing that needs to happen when the value is ready from storage. default is JSON.parse(). */
    parser?: (valueFromStorageSystem: string) => T;
  }
) => {
  return parseStorageValue(storageSystem.getItem(key), defaultValue, {
    key,
    parser,
  });
};

/**
 * State that syncs with local storage.
 *
 * Credits to Josh Comeau for inspiration, but note, this has been greatly
 * enhanced from the original idea.
 * @param defaultValue the first value to store. similar concept to useState.
 * @param key the key to store the value under in local storage.
 * @param parse optionally provide an override to the parsing that needs to happen when the value from storage is read. default is deserializeWithMapSupport().
 * @returns a tuple: the value and the value setter. similar in concept to base useState.
 */
export function useStickyState<T>(
  defaultValue: T,
  key: string,
  parser: (valueFromLocalStorage: string) => T = (value) =>
    deserializeWithMapSupport(value)
): [T, (newValueSetter: SetStateAction<T>) => void] {
  const instanceId = useRef(nanoid(4));
  const oldKey = usePrevious(key);

  const boundGetFromStorage = useCallback(
    () =>
      getFromStorage(key, defaultValue, {
        storageSystem: window.localStorage,
        parser,
      }),
    [defaultValue, key, parser]
  );
  const [value, setValue] = useState<T>(boundGetFromStorage);

  const setValueWithDispatchEvt = useCallback(
    (newValueSetter: SetStateAction<T>) => {
      const existingValue = window.localStorage.getItem(key);

      // always let React know about the update
      setValue((existingValueInState) => {
        let computedNewValue;

        if (newValueSetter instanceof Function) {
          computedNewValue = newValueSetter(existingValueInState);
        } else {
          computedNewValue = newValueSetter;
        }

        const stringifiedNewValue = containsMaps(computedNewValue)
          ? serializeWithMapSupport(computedNewValue)
          : JSON.stringify(computedNewValue);

        // only update storage if the new value is different
        if (existingValue !== stringifiedNewValue)
          window.localStorage.setItem(key, stringifiedNewValue);
        /**
         * storage evt is not fired in the window that made the change, so we
         * have to manually fire it so other instances of this hook bound to the
         * same storage key know about the update. note, we'll be ignoring this
         * duplicate storage event in this instance itself in the effect below
         * since we're updating React state here and syncing from evt is not
         * needed!
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event}
         */
        window.dispatchEvent(
          new StorageEvent("storage", {
            key,
            newValue: stringifiedNewValue,
            oldValue: existingValue,
            storageArea: window.localStorage,
            url: `self:instance:${instanceId.current}`,
          })
        );

        return computedNewValue;
      });
    },
    [key]
  );

  // subscribe to state update notifications and update the state
  useEffect(() => {
    const handleStorageEvent = (ev: StorageEvent) => {
      // don't do anything with events from this instance, since that's unnecessary syncing work!
      if (ev.url === `self:instance:${instanceId.current}`) return;

      if (
        ev.key !== key ||
        ev.oldValue === ev.newValue ||
        ev.storageArea !== window.localStorage
      ) {
        return;
      }

      // re-sync value in storage with the value in state
      setValue((existingValue) => {
        const existingValueSerialized = containsMaps(existingValue)
          ? serializeWithMapSupport(existingValue)
          : JSON.stringify(existingValue);

        return ev.newValue === existingValueSerialized
          ? existingValue
          : parseStorageValue(ev.newValue, defaultValue, { key, parser });
      });
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [key, defaultValue, parser]);

  // when key changes update the state
  useDeepCompareEffectNoCheck(() => {
    // if the new key is the same as the old key
    if (oldKey === key) return;

    const oldValue = window.localStorage.getItem(key);

    // don't sync with storage if the value is already up-to-date
    const valueSerialized = containsMaps(value)
      ? serializeWithMapSupport(value)
      : JSON.stringify(value);
    if (oldValue === valueSerialized) return;

    setValue(boundGetFromStorage());
  }, [oldKey, key, value, boundGetFromStorage]);

  return [value, setValueWithDispatchEvt];
}

/**
 * State that syncs with session storage.
 * @param defaultValue the first value to store. similar concept to useState.
 * @param key the key to store the value under in session storage.
 * @param parse optionally provide an override to the parsing that needs to happen when the value from storage is read. default is deserializeWithMapSupport().
 * @returns a tuple: the value and the value setter. similar in concept to base useState. Also, returns a function to update session storage directly
 */
export function useSessionStickyState<T>(
  defaultValue: T,
  key: string,
  parser: (valueFromSessionStorage: string) => T = (value) =>
    deserializeWithMapSupport(value)
): [T, (newValueSetter: SetStateAction<T>) => void] {
  const instanceId = useRef(nanoid(4));
  const oldKey = usePrevious(key);

  const boundGetFromStorage = () =>
    getFromStorage(key, defaultValue, {
      storageSystem: window.sessionStorage,
      parser,
    });
  const [value, setValue] = useState<T>(boundGetFromStorage);

  const setValueWithDispatchEvt = useCallback(
    (newValueSetter: SetStateAction<T>) => {
      const existingValue = window.sessionStorage.getItem(key);

      // always let React know about the update
      setValue((existingValueInState) => {
        let computedNewValue;

        if (newValueSetter instanceof Function) {
          computedNewValue = newValueSetter(existingValueInState);
        } else {
          computedNewValue = newValueSetter;
        }

        const stringifiedNewValue = containsMaps(computedNewValue)
          ? serializeWithMapSupport(computedNewValue)
          : JSON.stringify(computedNewValue);

        // only update storage if new value is different
        if (existingValue !== stringifiedNewValue)
          window.sessionStorage.setItem(key, stringifiedNewValue);
        /**
         * storage evt is not fired in the window that made the change, so we
         * have to manually fire it so other instances of this hook bound to the
         * same storage key know about the update. note, we'll be ignoring this
         * duplicate storage event in this instance itself in the effect below
         * since we're updating React state here and syncing from evt is not
         * needed!
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event}
         */
        window.dispatchEvent(
          new StorageEvent("storage", {
            key,
            newValue: stringifiedNewValue,
            oldValue: existingValue,
            storageArea: window.sessionStorage,
            url: `self:instance:${instanceId.current}`,
          })
        );

        return computedNewValue;
      });
    },
    [key]
  );

  // subscribe to state update notifications and update the state
  useEffect(() => {
    const handleStorageEvent = (ev: StorageEvent) => {
      // don't do anything with events from this instance, since that's unnecessary syncing work!
      if (ev.url === `self:instance:${instanceId.current}`) return;

      if (
        ev.key !== key ||
        ev.oldValue === ev.newValue ||
        ev.storageArea !== window.sessionStorage
      ) {
        return;
      }

      // re-sync value in storage with the value in state
      setValue((existingValue) => {
        const existingValueSerialized = containsMaps(existingValue)
          ? serializeWithMapSupport(existingValue)
          : JSON.stringify(existingValue);

        return ev.newValue === existingValueSerialized
          ? existingValue
          : parseStorageValue(ev.newValue, defaultValue, { key, parser });
      });
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [key, defaultValue, parser]);

  // when key changes update the state
  useEffect(() => {
    // if they new key is the same as the old key
    if (oldKey === key) return;

    const oldValue = window.sessionStorage.getItem(key);

    // don't sync with storage if the value is already up-to-date
    const valueSerialized = containsMaps(value)
      ? serializeWithMapSupport(value)
      : JSON.stringify(value);
    if (oldValue === valueSerialized) return;

    setValue(boundGetFromStorage());
  }, [oldKey, key, value, boundGetFromStorage]);

  return [value, setValueWithDispatchEvt];
}
